import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { requestJSON, retryIDSTeamRegistration } from "./api";
import { useI18n } from "./i18n";
import { DiagnosticText } from "./DiagnosticText";
import type { Translator } from "./i18n";
import { ManagementMessages } from "./managementUI";
export type RegistrationDetail = {
 operation_id: string; team_id: string; data_center_id: string; state: string; step: string;
 attempt_count: number; revision: number; error_code: string; updated_at: string;
};
export function registrationStepLabel(step: string, t: Translator): string {
 const labels: Record<string,[string,string]> = {
  reserved:["已保留 ID 与配额","ID and quota reserved"], database:["创建逻辑数据库","Creating logical database"], schema:["安装 Team Schema","Installing Team schema"], owner_membership:["初始化所有者与权限","Initializing owner and permissions"], binding:["发布数据库绑定","Publishing database binding"], placement:["分配 Pacture API","Assigning Pacture API"], ready:["全部阶段完成","All stages complete"]
 };
 return labels[step] ? t(...labels[step]) : step;
}
export function RegistrationFacts({ value }: { value: RegistrationDetail }) {
 const {t}=useI18n();
 const states: Record<string,[string,string]>={failed:["失败","Failed"],requested:["已请求","Requested"],running:["处理中","Processing"],ready:["注册已完成","Registration completed"]};
 return <>
  <p>{t("注册操作 ID","Registration operation ID")}: <code>{value.operation_id}</code></p>
  <dl className="monitor-summary">
   <div><dt>Team</dt><dd>{value.team_id}</dd></div>
   <div><dt>{t("状态","State")}</dt><dd>{t(...(states[value.state]??[value.state,value.state]))}</dd></div>
   <div><dt>{t("阶段","Step")}</dt><dd>{registrationStepLabel(value.step,t)}</dd></div>
   <div><dt>{t("尝试次数 / 版本","Attempts / revision")}</dt><dd>{value.attempt_count} / {value.revision}</dd></div>
  </dl>
  <p>{t("错误码","Error code")}: {value.error_code||"—"} · {t("更新于","Updated")}: {new Date(value.updated_at).toLocaleString(t("zh-CN","en-US"))}</p>
  {value.error_code ? <DiagnosticText value={{diagnostic:{code:value.error_code}}}/> : null}
  <p><Link to={`/team-runtime?team_id=${encodeURIComponent(value.team_id)}`}>{t("查看该团队运行状态","View this Team's runtime")}</Link></p>
 </>;
}
export function RegistrationLookup({csrfToken}:{csrfToken:string}) {
 const {t}=useI18n();
 const [query,setQuery]=useSearchParams();
 const [draft,setDraft]=useState(query.get("registration_id") ?? "");
 const [value,setValue]=useState<RegistrationDetail|null>(null);
 const [error,setError]=useState<unknown>(null);
 const [busy,setBusy]=useState(false);
 const [reason,setReason]=useState("");
 const [notice,setNotice]=useState("");
 const [canRetry,setCanRetry]=useState(false);
 const generation=useRef(0);
 useEffect(()=>()=>{generation.current++},[]);
 useEffect(()=>{const id=query.get("registration_id");if(id && /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(id)){setDraft(id);setReason("");void lookup(id)}},[query]);
 async function lookup(id:string,noticeText="") {
  const sequence=++generation.current;
  setBusy(true);setError(null);setValue(null);setCanRetry(false);setNotice(noticeText);
  try {const item=await requestJSON<RegistrationDetail>(`/api/v1/ids/team-registrations/${encodeURIComponent(id)}`);if(generation.current===sequence){setValue(item);setCanRetry(item.state==="failed")}}
  catch(cause){if(generation.current===sequence)setError(cause)}
  finally{if(generation.current===sequence)setBusy(false)}
 }
 async function retry(event:React.FormEvent) {
  event.preventDefault();if(!value||!canRetry||busy||!reason.trim())return;
  const current=value;setBusy(true);setError(null);setCanRetry(false);
  try {
   await retryIDSTeamRegistration(csrfToken,reason.trim(),current.operation_id,current.revision);
   setReason("");
   await lookup(current.operation_id,t("重试已受理；以下是最新注册状态，受理不代表团队已就绪。","Retry accepted. The latest registration state is shown below; acceptance does not mean the Team is ready."));
  } catch(cause){setError(cause);setNotice(t("结果未确认，请先重新查询原注册记录，再决定是否重试。","Outcome unconfirmed. Reload the original registration before retrying."));setBusy(false)}
 }
 return <section className="panel">
  <div className="panel-header"><h2>{t("按操作 ID 查询注册记录","Find registration by operation ID")}</h2></div>
  <p className="monitor-note">{t("精确查询当前数据中心的持久记录，不受近期列表条数限制。","Exact lookup in this DC, independent of the recent-list limit.")}</p>
  <form className="directory-toolbar" onSubmit={event=>{event.preventDefault();setReason("");if(query.get("registration_id")===draft.trim())void lookup(draft.trim());else setQuery({registration_id:draft.trim()})}}>
   <label>{t("注册操作 ID","Registration operation ID")}<input required pattern="[A-Za-z0-9][A-Za-z0-9_-]{0,127}" value={draft} disabled={busy} onChange={event=>{setDraft(event.target.value);setCanRetry(false)}} /></label>
   <button className="secondary-button" disabled={busy||!draft.trim()}>{busy?t("查询或处理中…","Working…"):t("查询 / 刷新","Find / refresh")}</button>
  </form>
  <ManagementMessages error={error}/>
  {notice?<p role="status">{notice}</p>:null}
  {value?<><RegistrationFacts value={value}/>{value.state==="failed"?<form className="directory-toolbar" onSubmit={retry}>
   <label>{t("重试原因","Retry reason")}<input required maxLength={512} value={reason} disabled={busy||!canRetry} onChange={event=>setReason(event.target.value)}/></label>
   <button className="primary-button" disabled={busy||!canRetry||!reason.trim()}>{t("重试此注册操作","Retry this registration")}</button>
  </form>:null}</>:null}
 </section>;
}
